// Seed content lifted verbatim from the claude.ai/design project
// ("Janatha Hrudayalaya Foundation.dc.html"). It is written to Firestore once
// by functions/scripts/seed-content.mjs, and the client falls back to it when
// a document has not been created yet, so the site never renders empty.
//
// Page structure lives in code; everything here is editable from /admin.

export const settings = {
  name: 'Janatha Hrudayalaya Foundation',
  tagline: 'Caring Hearts · Saving Lives',
  // Pages kept out of the public site. They stay fully editable in /admin, and
  // their content is untouched -- removing an id here puts the page back.
  hiddenPages: ['camps', 'cultural'],
  addressLines: [
    'H.No. 1-41, Shankarapur, Shamshabad, Cherlaguda,',
    'K.V. Rangareddy, Chinnagolkonda,',
    'Telangana – 501218, India',
  ],
  footerAddressLines: ['H.No. 1-41, Shankarapur,', 'Shamshabad, Cherlaguda,', 'Telangana – 501218'],
  phone: '',
  email: '',
  instagram: 'janatha_hrudayalaya_foundation',
  instagramUrl: 'https://www.instagram.com/janatha_hrudayalaya_foundation/',
  certification: 'ISO 9001:2015',
  certificationBody: 'Revolutionary Certifications Services',
  certificationNote:
    'Quality Management System certified to ISO 9001:2015 by Revolutionary Certifications Services.',
  copyright: '© 2026 Janatha Hrudayalaya Foundation. All rights reserved.',
  // Search and link previews. The description is the fallback for any page
  // without its own; the share image is the card shown when a page with no
  // photograph of its own is shared.
  seoDescription:
    'Free health camps, life-support training and welfare programmes for farmers, orphans, women and school children across Ranga Reddy district, Telangana. ISO 9001:2015 certified.',
  shareImage: '',
};

export const home = {
  metaTitle: 'Janatha Hrudayalaya Foundation | Free Health Camps in Telangana',
  metaDescription:
    'Free cardiac and skin screening camps, CPR training and welfare programmes across Ranga Reddy district, Telangana — run by practising specialists. ISO 9001:2015 certified.',
  badge: 'ISO 9001:2015 certified',
  title: 'Free medical care,\nbrought to the village.',
  lead: 'Janatha Hrudayalaya Foundation runs free health camps, life-support training and welfare programmes for farmers, orphans, women and school children across Ranga Reddy district and beyond. Founded and run by practising specialists.',
  primaryCta: 'See our health camps',
  secondaryCta: 'Events & gallery',
  slides: [
    {
      hint: 'photo — cardiac screening camp\nKothur village (1600×1200)',
      title: 'Cardiac screening at Kothur',
      caption: 'ECG and blood pressure checks for 240 farmers, free of cost.',
      image: '',
    },
    {
      hint: 'photo — CPR training session\nschool teachers (1600×1200)',
      title: 'Life support training',
      caption: 'Teachers and volunteers trained in CPR and AED use.',
      image: '',
    },
    {
      hint: "photo — children's health check\norphanage visit (1600×1200)",
      title: "Care for children's homes",
      caption: 'General, skin and nutrition screening for resident children.',
      image: '',
    },
    {
      hint: 'photo — village kabaddi final\ncommunity ground (1600×1200)',
      title: 'Sports bring the village out',
      caption: 'Tournaments and folk evenings that open the door to health work.',
      image: '',
    },
  ],
  pillars: [
    {
      title: 'Always free',
      body: 'No consultation fee, no registration charge, no cost for the medicines we hand out.',
    },
    {
      title: 'Run by specialists',
      body: 'Camps are staffed by practising consultants, not by volunteers alone.',
    },
    {
      title: 'We travel to you',
      body: "Villages, schools, children's homes and farm settlements across Ranga Reddy district.",
    },
  ],
  work: [
    {
      title: 'Free health camps',
      body: 'Cardiac and skin screening, blood pressure and sugar checks, and basic life support training in CPR and AED use.',
    },
    {
      title: 'Women and children',
      body: 'Support for women, school children, orphans and underprivileged groups — health, counselling and essentials.',
    },
    {
      title: 'Farmers and environment',
      body: 'Protecting the environment and supporting farmers towards sustainable livelihoods.',
    },
    {
      title: 'Sports and culture',
      body: 'Village tournaments, folk arts and school programmes that build community around health.',
    },
  ],
  instagramPosts: [
    { hint: 'ig post 1', image: '', url: '' },
    { hint: 'ig post 2', image: '', url: '' },
    { hint: 'ig post 3', image: '', url: '' },
    { hint: 'ig post 4', image: '', url: '' },
    { hint: 'ig post 5', image: '', url: '' },
    { hint: 'ig post 6', image: '', url: '' },
  ],
};

export const founders = {
  items: [
    {
      name: 'Dr. Malreddy Hanumantha Reddy',
      role: 'Senior Interventional Cardiologist',
      photoHint: 'portrait — Dr. Hanumantha Reddy',
      image: '',
      bio: 'Senior Consultant Interventional Cardiologist at STAR Hospitals, Banjara Hills, with decades of experience in complex cardiac intervention and advanced cardiac imaging. DrNB in Cardiology from Narayana Hrudayalaya, Bengaluru, and a cardiac imaging fellowship at the National Heart Centre, Singapore. Recipient of the Medical Excellence Award from the Telangana Governor and the Indian Red Cross Society, 2025.',
    },
    {
      name: 'Dr. M. Methini Reddy',
      role: 'Senior Dermatologist and Cosmetologist',
      photoHint: 'portrait — Dr. Methini Reddy',
      image: '',
      bio: 'Dermatologist, Trichologist and Cosmetologist with eleven years of practice in Hyderabad, working across medical and aesthetic dermatology with a particular focus on hair loss. MD in Dermatology, Venereology and Leprosy, with an observership at the National Skin Centre, Singapore. She favours a diagnosis-first approach so that results hold up over time.',
    },
  ],
};

export const camps = {
  metaTitle: 'Free health camps in Ranga Reddy district',
  metaDescription:
    'Free village health camps near Shamshabad: cardiac screening, ECG, skin and hair consultation, blood pressure and sugar checks, and CPR training.',
  lead: 'Every camp is free. Cardiac screening, skin and hair consultation, blood pressure and sugar checks, ECG where available, and basic life support training in CPR and AED use. We come to the village; nobody travels to the city.',
  items: [
    {
      status: 'Upcoming',
      date: 'Sunday, 11 October 2026 · 9am – 3pm',
      village: 'Kothur',
      venue: 'Gram Panchayat hall, Kothur mandal',
      coords: '17.15° N, 78.28° E',
      specialists:
        'Dr. M. Hanumantha Reddy (cardiology), Dr. M. Methini Reddy (dermatology), two general physicians, three paramedics',
      services: [
        'Cardiac screening',
        'ECG',
        'Blood pressure',
        'Blood sugar',
        'Skin & hair',
        'CPR training',
      ],
      photoHint: 'cover photo — Kothur camp',
      image: '',
    },
    {
      status: 'Upcoming',
      date: 'Sunday, 8 November 2026 · 9am – 2pm',
      village: 'Shankarapur',
      venue: 'Community hall, Shamshabad mandal',
      coords: '17.24° N, 78.40° E',
      specialists: 'Dr. M. Methini Reddy (dermatology), one general physician, two paramedics',
      services: [
        'Skin & hair',
        'Blood pressure',
        'Blood sugar',
        "Women's health",
        'Medicine distribution',
      ],
      photoHint: 'cover photo — Shankarapur camp',
      image: '',
    },
    {
      status: 'Completed',
      date: 'Sunday, 17 August 2026 · 9am – 4pm',
      village: 'Cherlaguda',
      venue: 'Zilla Parishad school grounds',
      coords: '17.21° N, 78.35° E',
      specialists:
        'Dr. M. Hanumantha Reddy (cardiology), Dr. M. Methini Reddy (dermatology), four volunteers',
      services: ['Cardiac screening', 'ECG', 'Skin & hair', 'Blood pressure', 'BLS awareness'],
      photoHint: '6 photos — Cherlaguda camp',
      image: '',
    },
  ],
};

export const events = {
  metaTitle: 'Events, training days and awareness drives',
  metaDescription:
    'CPR and life-support training, health screenings and awareness drives run by Janatha Hrudayalaya Foundation across Ranga Reddy district.',
  lead: 'Training days, awareness drives, felicitations and community gatherings. Open one to see its photos.',
  items: [
    {
      id: 'cpr-2026',
      title: 'Basic Life Support & CPR training for school teachers',
      date: '14 March 2026',
      status: 'Upcoming',
      place: 'ZP High School, Shamshabad',
      blurb:
        'A full-day hands-on session in chest compressions, recovery position and AED use.',
      detail:
        'Teachers are the first responders in any school emergency. This full-day session covers chest compressions, rescue breathing, the recovery position and AED use, with mannequin practice for every participant. Certificates are issued on completion.',
      image: '',
      photos: [
        'photo — mannequin practice',
        'photo — AED demonstration',
        'photo — certificate handover',
        'photo — instructor briefing',
        'photo — classroom wide shot',
        'photo — participants',
      ],
    },
    {
      id: 'orphanage-2025',
      title: "Health screening at a children's home",
      date: '9 November 2025',
      status: 'Past',
      place: 'Chinnagolkonda',
      blurb: 'General check-up, skin and nutrition screening for 120 children.',
      detail:
        'A morning of general check-ups, skin and hair consultation and nutrition screening for resident children. Follow-up referrals were arranged for eight children needing specialist review.',
      image: '',
      photos: [
        'photo — registration desk',
        'photo — paediatric check-up',
        'photo — skin consultation',
        'photo — volunteers',
        'photo — group photo',
        'photo — medicine distribution',
      ],
    },
    {
      id: 'farmers-2025',
      title: "Farmers' heart health awareness drive",
      date: '22 August 2025',
      status: 'Past',
      place: 'Cherlaguda village square',
      blurb: 'Blood pressure and ECG screening with a talk on early warning signs.',
      detail:
        'Held at the village square on a market day, this drive combined blood pressure and ECG screening with a plain-language talk on the early warning signs of heart disease. Over two hundred farmers and their families attended.',
      image: '',
      photos: [
        'photo — ECG screening',
        'photo — BP check',
        'photo — awareness talk',
        'photo — crowd',
        'photo — doctor consultation',
        'photo — take-home leaflets',
      ],
    },
    {
      id: 'women-2025',
      title: "Women's health and anaemia camp",
      date: '8 March 2025',
      status: 'Past',
      place: 'Shankarapur community hall',
      blurb: 'Haemoglobin testing, counselling and iron supplementation.',
      detail:
        "Marking International Women's Day, the camp offered haemoglobin testing, general counselling, skin consultation and iron supplementation, with dietary guidance in the local language.",
      image: '',
      photos: [
        'photo — haemoglobin testing',
        'photo — counselling',
        'photo — queue',
        'photo — supplement handout',
        'photo — team',
        'photo — closing',
      ],
    },
  ],
};

export const gallery = {
  metaTitle: 'Photo gallery',
  metaDescription:
    "Photographs from Janatha Hrudayalaya Foundation's free health camps, training days, events and cultural programmes in Telangana.",
  lead: 'Photographs from camps, events and cultural programmes.',
  groups: ['All', 'Health camps', 'Events', 'Sports & culture'],
  // Items whose group is "Events" are generated from the events above, so the
  // gallery cannot drift from the event records.
  items: [
    { hint: 'photo — cardiac camp, Kothur', caption: 'Health camps · Kothur, 2025', group: 'Health camps', image: '' },
    { hint: 'photo — mobile ECG unit', caption: 'Health camps · 2025', group: 'Health camps', image: '' },
    { hint: 'photo — village registration desk', caption: 'Health camps · Cherlaguda, 2025', group: 'Health camps', image: '' },
    { hint: 'photo — dermatology consultation queue', caption: 'Health camps · 2025', group: 'Health camps', image: '' },
    { hint: 'photo — CPR mannequin drill', caption: 'Health camps · Shamshabad, 2026', group: 'Health camps', image: '' },
    { hint: 'photo — medicine counter', caption: 'Health camps · 2025', group: 'Health camps', image: '' },
    { hint: 'photo — kabaddi final', caption: 'Village kabaddi tournament', group: 'Sports & culture', image: '' },
    { hint: 'photo — folk dance evening', caption: 'Folk arts evening', group: 'Sports & culture', image: '' },
    { hint: 'photo — school sports day', caption: 'School sports day', group: 'Sports & culture', image: '' },
  ],
};

export const cultural = {
  metaTitle: 'Sports and culture',
  metaDescription:
    'Village kabaddi tournaments, folk arts evenings, school sports days and street theatre on health, run by Janatha Hrudayalaya Foundation.',
  lead: 'Promoting sports and cultural activities for community development is part of our certified scope. Village tournaments, folk arts and school programmes bring people together — and give us a way to reach families long before anyone is ill.',
  items: [
    {
      kind: 'Sport',
      title: 'Village kabaddi tournament',
      photoHint: 'photo — kabaddi final',
      image: '',
      body: 'An inter-village tournament run each summer, with a health desk on the sidelines checking blood pressure between matches.',
    },
    {
      kind: 'Folk arts',
      title: 'Folk arts evening',
      photoHint: 'photo — folk dance performance',
      image: '',
      body: 'Local performers, traditional dance and music. Free to attend, held in the village square after harvest.',
    },
    {
      kind: 'Schools',
      title: 'School sports day',
      photoHint: 'photo — school sports day',
      image: '',
      body: 'Athletics and team games for government school children, with basic fitness screening for every participant.',
    },
    {
      kind: 'Awareness',
      title: 'Street theatre on health',
      photoHint: 'photo — street play performance',
      image: '',
      body: 'Short plays in the local language on heart health, hygiene and early warning signs — reaching people who will not read a leaflet.',
    },
  ],
};

export const contact = {
  metaTitle: 'Contact us',
  metaDescription:
    'Request a free health camp in your village, volunteer, or join as a doctor or paramedic. Contact Janatha Hrudayalaya Foundation, Shamshabad, Telangana.',
  lead: 'Want a camp in your village, or want to volunteer? Write to us.',
  reasons: [
    'Request a camp in our village',
    'Volunteer with the foundation',
    'Join as a doctor or paramedic',
    'Cultural or sports programme',
    'Something else',
  ],
  thanks: 'Thank you. Your message reaches the admin inbox.',
};

// The document id under `content/` for each block above.
export const SEED = { settings, home, founders, camps, events, gallery, cultural, contact };
